/**
 * Nested dialog that collects free-text feedback after a thumbs-down on a
 * clinical document. Uses its own oasis dialog id so it can stack on top of
 * {@link DocViewerModal} without touching the Redux modal slice.
 *
 * @module documentFeedback/DocumentFeedbackModal
 */
import { useEffect, useRef, useState } from "react";
import { closeModal, Modal, openModal } from "risa-oasis-ui_v2";
import { ModalId } from "../../enums/modalId";
import { logError } from "../../utils/customLogger";
import {
  DOCUMENT_FEEDBACK_MODAL_CANCEL,
  DOCUMENT_FEEDBACK_MODAL_PLACEHOLDER,
  DOCUMENT_FEEDBACK_MODAL_PROMPT,
  DOCUMENT_FEEDBACK_MODAL_SAVE,
  DOCUMENT_FEEDBACK_MODAL_TITLE,
} from "./constants";

/** Props for {@link DocumentFeedbackModal}. */
export interface DocumentFeedbackModalProps {
  /** Whether the dialog should be open. */
  isOpen: boolean;
  /** Closes the dialog without submitting. */
  onClose: () => void;
  /** Submits the trimmed comment. */
  onSubmit: (comment: string) => void;
}

/**
 * Renders the thumbs-down feedback dialog.
 *
 * The `<Modal>` stays mounted while closed: oasis resolves the dialog element
 * by id when closing, so unmounting it first would leave nothing to close.
 *
 * @param props - Open state and close/submit handlers
 * @returns The oasis modal
 */
const DocumentFeedbackModal = ({
  isOpen,
  onClose,
  onSubmit,
}: DocumentFeedbackModalProps) => {
  const isModalOpenRef = useRef(false);
  const [comment, setComment] = useState("");

  useEffect(() => {
    if (isOpen && !isModalOpenRef.current) {
      setComment("");
      openModal(ModalId.DOCUMENT_FEEDBACK_MODAL);
      isModalOpenRef.current = true;
    } else if (!isOpen && isModalOpenRef.current) {
      closeModal(ModalId.DOCUMENT_FEEDBACK_MODAL);
      isModalOpenRef.current = false;
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      if (isModalOpenRef.current) {
        try {
          closeModal(ModalId.DOCUMENT_FEEDBACK_MODAL);
        } catch (error) {
          logError(error as Error, "Error closing document feedback modal");
        }
        isModalOpenRef.current = false;
      }
    },
    [],
  );

  const trimmed = comment.trim();

  return (
    <Modal
      dialogId={ModalId.DOCUMENT_FEEDBACK_MODAL}
      title={DOCUMENT_FEEDBACK_MODAL_TITLE}
      onCancel={onClose}
      onClose={onClose}
      onSave={() => onSubmit(trimmed)}
      cancelText={DOCUMENT_FEEDBACK_MODAL_CANCEL}
      saveButtonText={DOCUMENT_FEEDBACK_MODAL_SAVE}
      disableSave={!trimmed}
    >
      <div className="flex flex-col gap-2">
        <div className="text-small font-normal text-primaryGray-2">
          {DOCUMENT_FEEDBACK_MODAL_PROMPT}
        </div>
        <textarea
          className="h-24 w-full resize-none rounded-md border border-primaryGray-14 p-3 text-small placeholder-gray-400 focus:outline-none"
          placeholder={DOCUMENT_FEEDBACK_MODAL_PLACEHOLDER}
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
      </div>
    </Modal>
  );
};

export default DocumentFeedbackModal;
